import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Collapse,
  DatePicker,
  Descriptions,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Upload,
} from "antd";
import { ArrowLeftOutlined, PlusOutlined, UploadOutlined, WarningOutlined } from "@ant-design/icons";
import * as dayjs from "dayjs";
import { useFirestore } from "react-redux-firebase";
import { useTranslation } from "react-i18next";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";

import {
  FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE,
  FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE,
  FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE,
  FIRESTORE_PURCHASE_ORDERS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { formatPriceByCurrency } from "../../../../../functions/formatters";
import urls from "../../../../../urls";
import { useSelector } from "react-redux";
import {
  usePurchaseOrderInvoices,
  usePurchaseOrderPayments,
  usePurchaseOrders,
  useProducts,
  useSuppliers,
  usePurchaseOrderShipments,
} from "../../../../../data/hooks";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";

const { TextArea } = Input;
const { Panel } = Collapse;

const invoiceStatusColors = {
  draft: "default",
  submitted: "processing",
  approved: "blue",
  partially_paid: "orange",
  paid: "green",
  disputed: "red",
};

const invoiceStatusLabels = {
  draft: "Concept",
  submitted: "Ingediend",
  approved: "Goedgekeurd",
  partially_paid: "Deels betaald",
  paid: "Betaald",
  disputed: "Betwist",
};

const PurchaseOrderInvoiceDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [paymentDate, setPaymentDate] = useState(dayjs());
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentExchangeRate, setPaymentExchangeRate] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [editedInvoiceLines, setEditedInvoiceLines] = useState([]);
  const [editedAdditionalCosts, setEditedAdditionalCosts] = useState([]);
  const [editedDiscountLines, setEditedDiscountLines] = useState([]);
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newLineQuantity, setNewLineQuantity] = useState(1);
  const [newLineUnitPrice, setNewLineUnitPrice] = useState(0);
  const [newLineDiscount, setNewLineDiscount] = useState(0);
  const [legacyPayments, setLegacyPayments] = useState([]);
  const [syncingToExact, setSyncingToExact] = useState(false);
  const email = useSelector((state) => state.firebaseReducer.auth.email);

  // Use hooks for invoices, shipments, payments, and purchase orders
  const { invoices: allInvoices } = usePurchaseOrderInvoices();
  const { shipments: allShipments } = usePurchaseOrderShipments();
  const { payments } = usePurchaseOrderPayments({ invoiceId: id });
  const { purchaseOrders } = usePurchaseOrders();
  const { suppliers } = useSuppliers();

  // Find the specific invoice by ID
  const invoice = allInvoices.find((inv) => inv.id === id);

  // Find the shipment by invoice.shipmentId
  const shipment = allShipments.find((ship) => ship.id === invoice?.shipmentId);

  // Get current supplier to extract productIds
  const currentSupplier = suppliers.find((s) => s.uid === shipment?.supplierId);
  const { products } = useProducts({ productIds: currentSupplier?.productIds || [] });

  // Use invoice's own currency
  const invoiceCurrency = invoice?.currency || "USD";

  // Determine if invoice is out of sync with Exact (compare last history entry with exactSyncedAt)
  const exactOutOfSync = useMemo(() => {
    if (!invoice?.exactPurchaseInvoiceId || !invoice?.exactSyncedAt) return false;
    if (!invoice?.history || invoice.history.length === 0) return false;

    const lastHistoryEntry = invoice.history[invoice.history.length - 1];
    return lastHistoryEntry.changedAt > invoice.exactSyncedAt;
  }, [invoice]);

  // Load legacy payments from purchase orders using hook data
  useEffect(() => {
    if (!shipment?.sourceOrders || !purchaseOrders?.length) return;

    const allLegacyPayments = [];
    const orderIds = shipment.sourceOrders.map((so) => so.purchaseOrderId).filter(Boolean);

    for (const orderId of orderIds) {
      const order = purchaseOrders.find((po) => po.id === orderId);
      if (order && order.payments && Array.isArray(order.payments)) {
        for (const payment of order.payments) {
          allLegacyPayments.push({
            ...payment,
            sourceOrderNumber: order.orderNumber,
            sourceOrderId: orderId,
            isLegacy: true,
          });
        }
      }
    }

    setLegacyPayments(allLegacyPayments);
  }, [shipment, purchaseOrders]);

  // Combine legacy payments with invoice payments (legacy first)
  const allPayments = [
    ...legacyPayments.map((p) => ({ ...p, id: `legacy-${p.sourceOrderId}-${p.paidAt}` })),
    ...(payments || []),
  ];

  // Helper function to get original unit price from shipment items by index
  const getOriginalUnitPrice = (index) => {
    if (!shipment?.items || index === null || index === undefined) return null;
    const shipmentItem = shipment.items[index];
    return shipmentItem ? shipmentItem.unitPrice : null;
  };

  // Helper function to get original quantity from shipment items by index
  const getOriginalQuantity = (index) => {
    if (!shipment?.items || index === null || index === undefined) return null;
    const shipmentItem = shipment.items[index];
    return shipmentItem ? shipmentItem.quantity : null;
  };

  // Helper function to add history entry for invoice changes
  const addHistoryEntry = async (field, oldValue, newValue, action, metadata = null) => {
    if (!invoice) return;

    const historyEntry = {
      field,
      oldValue,
      newValue,
      action,
      metadata,
      changedAt: dayjs().unix(),
      changedBy: email,
    };

    const history = invoice.history || [];
    await firestore
      .collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE)
      .doc(invoice.id)
      .update({
        history: [...history, historyEntry],
      });
  };

  // Navigate away if invoice not found
  useEffect(() => {
    if (allInvoices.length > 0 && !invoice) {
      message.error(t("invoice.not_found"));
      navigate(urls.purchaseOrderInvoicesList);
    }
    setLoading(false);
  }, [allInvoices, invoice]);

  const handleUpdateInvoiceStatus = async (newStatus) => {
    if (!invoice) return;
    try {
      setSaving(true);
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      await addHistoryEntry("status", invoice.status, newStatus, "status_change");
      message.success("Factuurstatus bijgewerkt");
    } catch (error) {
      console.error("Error updating invoice status:", error);
      message.error("Fout bij bijwerken factuurstatus");
    } finally {
      setSaving(false);
    }
  };

  const handleSyncToExact = async () => {
    if (!shipment?.sourceOrders?.[0]?.purchaseOrderNumber) {
      message.error("Geen ordernummer gevonden voor Exact synchronisatie");
      return;
    }

    setSyncingToExact(true);
    try {
      const idToken = await getFirebaseIDToken();
      const orderNumber = shipment.sourceOrders[0].purchaseOrderNumber;
      const endpoint = `${process.env.REACT_APP_API_URL || ""}/api/exact/purchase-orders/${orderNumber}/invoice`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken,
        },
        body: JSON.stringify({
          invoiceId: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          shipmentId: invoice.shipmentId,
          totalAmount: invoice.totalAmount,
          pdfUrl: invoice.pdfUrl || null,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data?.exactPurchaseInvoiceId) {
        await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
          exactPurchaseInvoiceId: data.exactPurchaseInvoiceId,
          exactSyncedAt: dayjs().unix(),
        });
        message.success("Factuur succesvol gesynchroniseerd met Exact");
      } else {
        message.warning("Factuur verzonden naar Exact, maar geen ID ontvangen");
      }
    } catch (error) {
      console.error("Error syncing to Exact:", error);
      message.error(`Fout bij synchroniseren met Exact: ${error.message}`);
    } finally {
      setSyncingToExact(false);
    }
  };

  const handleStartEditingInvoice = () => {
    setEditedInvoiceLines(JSON.parse(JSON.stringify(invoice.lines || [])));
    setEditedAdditionalCosts(JSON.parse(JSON.stringify(invoice.additionalCosts || [])));
    setEditedDiscountLines(JSON.parse(JSON.stringify(invoice.discountLines || [])));
    setEditingInvoice(true);
  };

  const handleCancelEditingInvoice = () => {
    setEditingInvoice(false);
    setEditedInvoiceLines([]);
    setEditedAdditionalCosts([]);
    setEditedDiscountLines([]);
  };

  const handleAddDiscountLine = () => {
    setEditedDiscountLines([
      ...editedDiscountLines,
      {
        id: `discount_${Date.now()}`,
        description: "",
        amount: 0,
        createdAt: dayjs().unix(),
        createdBy: email,
      },
    ]);
  };

  const handleRemoveDiscountLine = (discountId) => {
    setEditedDiscountLines(editedDiscountLines.filter((d) => d.id !== discountId));
  };

  const handleDiscountLineChange = (discountId, field, value) => {
    setEditedDiscountLines(
      editedDiscountLines.map((d) => {
        if (d.id !== discountId) return d;
        if (field === "amount") {
          const num = value === null || value === undefined ? 0 : -Math.abs(value);
          return { ...d, amount: num };
        }
        return { ...d, [field]: value };
      }),
    );
  };

  const handleAddInvoiceLine = () => {
    if (!selectedProduct) {
      message.error("Selecteer een product");
      return;
    }

    const newLine = {
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      productColor: selectedProduct.color,
      productCode: selectedProduct.code,
      quantity: newLineQuantity,
      unitPrice: newLineUnitPrice,
      discountPercentage: newLineDiscount,
      totalPrice: newLineQuantity * newLineUnitPrice * (1 - newLineDiscount / 100),
    };

    setEditedInvoiceLines([...editedInvoiceLines, newLine]);
    setShowAddLineModal(false);
    setSelectedProduct(null);
    setNewLineQuantity(1);
    setNewLineUnitPrice(0);
    setNewLineDiscount(0);
    message.success("Factuurregel toegevoegd");
  };

  const handleRemoveInvoiceLine = (index) => {
    const updatedLines = editedInvoiceLines.filter((_, i) => i !== index);
    setEditedInvoiceLines(updatedLines);
    message.success("Factuurregel verwijderd");
  };

  const handleOpenAddLineModal = () => {
    setSelectedProduct(null);
    setNewLineQuantity(1);
    setNewLineUnitPrice(0);
    setNewLineDiscount(0);
    setShowAddLineModal(true);
  };

  const handleCloseAddLineModal = () => {
    setShowAddLineModal(false);
    setSelectedProduct(null);
    setNewLineQuantity(1);
    setNewLineUnitPrice(0);
    setNewLineDiscount(0);
  };

  const handleProductSelect = (productId) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setNewLineUnitPrice(product.purchasePrice || 0);
    }
  };

  const handleInvoiceLineChange = (index, field, value) => {
    const updated = [...editedInvoiceLines];
    const line = { ...updated[index] };

    if (field === "quantity" || field === "unitPrice" || field === "discountPercentage") {
      line[field] = value;
      // Recalculate totalPrice for this line
      const qty = field === "quantity" ? value : line.quantity || 0;
      const price = field === "unitPrice" ? value : line.unitPrice || 0;
      const discount = field === "discountPercentage" ? value : line.discountPercentage || 0;
      line.totalPrice = qty * price * (1 - discount / 100);
    } else {
      line[field] = value;
    }

    updated[index] = line;
    setEditedInvoiceLines(updated);
  };

  const handleAdditionalCostChange = (index, field, value) => {
    const updated = [...editedAdditionalCosts];
    updated[index] = { ...updated[index], [field]: value };
    setEditedAdditionalCosts(updated);
  };

  const handleSaveInvoiceEdits = async () => {
    try {
      setSaving(true);

      // Validate discount lines
      for (const d of editedDiscountLines) {
        if (!d.description || !d.description.trim()) {
          message.error("Geef een omschrijving voor elke kortingsregel");
          setSaving(false);
          return;
        }
        if (!d.amount || d.amount >= 0) {
          message.error("Kortingsbedragen moeten negatief zijn");
          setSaving(false);
          return;
        }
      }

      // Normalize discount amounts to be strictly negative
      const normalizedDiscountLines = editedDiscountLines.map((d) => ({
        ...d,
        amount: -Math.abs(d.amount || 0),
      }));

      // Recalculate totals
      const subtotalProducts = editedInvoiceLines.reduce((sum, line) => sum + (line.totalPrice || 0), 0);
      const subtotalAdditionalCosts = editedAdditionalCosts.reduce((sum, cost) => sum + (cost.amount || 0), 0);
      const subtotalDiscounts = normalizedDiscountLines.reduce((sum, d) => sum + (d.amount || 0), 0);
      const totalAmount = subtotalProducts + subtotalAdditionalCosts + subtotalDiscounts;
      const currentTotalPaid = invoice.totalPaid || 0;
      const amountDue = totalAmount - currentTotalPaid;

      // Track specific line changes
      const lineChanges = [];
      const oldLines = invoice.lines || [];
      const newLines = editedInvoiceLines;

      // Create maps for easier comparison (use productId instead of productCode)
      const oldLinesMap = new Map(oldLines.map((line, idx) => [line.productId, { ...line, originalIndex: idx }]));
      const newLinesMap = new Map(newLines.map((line, idx) => [line.productId, { ...line, originalIndex: idx }]));

      // Find added lines
      newLines.forEach((newLine, idx) => {
        if (!oldLinesMap.has(newLine.productId)) {
          lineChanges.push(
            `+ Regel ${idx + 1}: ${newLine.productName} (${newLine.productId}) - ${newLine.quantity}x ${formatPriceByCurrency(newLine.unitPrice, invoiceCurrency)}`,
          );
        } else {
          const oldLine = oldLinesMap.get(newLine.productId);
          // Check for changes in quantity
          if (oldLine.quantity !== newLine.quantity) {
            lineChanges.push(`~ ${newLine.productName}: aantal ${oldLine.quantity} → ${newLine.quantity}`);
          }
          // Check for changes in unit price
          if (oldLine.unitPrice !== newLine.unitPrice) {
            lineChanges.push(
              `~ ${newLine.productName}: prijs ${formatPriceByCurrency(oldLine.unitPrice, invoiceCurrency)} → ${formatPriceByCurrency(newLine.unitPrice, invoiceCurrency)}`,
            );
          }
          // Check for changes in discount
          if (oldLine.discountPercentage !== newLine.discountPercentage) {
            lineChanges.push(
              `~ ${newLine.productName}: korting ${oldLine.discountPercentage}% → ${newLine.discountPercentage}%`,
            );
          }
        }
      });

      // Find removed lines
      oldLines.forEach((oldLine) => {
        if (!newLinesMap.has(oldLine.productId)) {
          lineChanges.push(`- ${oldLine.productName} verwijderd`);
        }
      });

      // Track discount line changes
      const oldDiscountLines = invoice.discountLines || [];
      const oldDiscountMap = new Map(oldDiscountLines.map((d) => [d.id, d]));
      const newDiscountMap = new Map(normalizedDiscountLines.map((d) => [d.id, d]));
      normalizedDiscountLines.forEach((d) => {
        const old = oldDiscountMap.get(d.id);
        if (!old) {
          lineChanges.push(
            `+ Korting: ${d.description} (${formatPriceByCurrency(d.amount, invoiceCurrency)})`,
          );
        } else if (old.description !== d.description || old.amount !== d.amount) {
          lineChanges.push(
            `~ Korting: ${old.description} ${formatPriceByCurrency(old.amount, invoiceCurrency)} → ${d.description} ${formatPriceByCurrency(d.amount, invoiceCurrency)}`,
          );
        }
      });
      oldDiscountLines.forEach((d) => {
        if (!newDiscountMap.has(d.id)) {
          lineChanges.push(`- Korting verwijderd: ${d.description}`);
        }
      });

      // Update invoice
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        lines: editedInvoiceLines,
        additionalCosts: editedAdditionalCosts,
        discountLines: normalizedDiscountLines,
        subtotalProducts,
        subtotalAdditionalCosts,
        subtotalDiscounts,
        totalAmount,
        amountDue,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      // Combine all changes into single history entry
      const allChanges = [...lineChanges];
      if (invoice.totalAmount !== totalAmount) {
        allChanges.push(
          `Totaalbedrag: ${formatPriceByCurrency(invoice.totalAmount, invoiceCurrency)} → ${formatPriceByCurrency(totalAmount, invoiceCurrency)}`,
        );
      }

      if (allChanges.length > 0) {
        await addHistoryEntry(
          "invoice",
          JSON.stringify({ lines: invoice.lines, totalAmount: invoice.totalAmount }),
          JSON.stringify({ lines: editedInvoiceLines, totalAmount }),
          "invoice_edit",
          allChanges.join("; "),
        );
      }

      message.success("Factuurwijzigingen opgeslagen");
      setEditingInvoice(false);
      setEditedInvoiceLines([]);
      setEditedAdditionalCosts([]);
      setEditedDiscountLines([]);
    } catch (error) {
      console.error("Error saving invoice edits:", error);
      message.error("Fout bij opslaan factuurwijzigingen");
    } finally {
      setSaving(false);
    }
  };

  const resetPaymentForm = () => {
    setPaymentAmount(null);
    setPaymentDate(dayjs());
    setPaymentReference("");
    setPaymentNotes("");
    setPaymentProofFile(null);
    setPaymentExchangeRate(null);
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentAmount(payment.amount);
    setPaymentDate(payment.paymentDate ? dayjs.unix(payment.paymentDate) : dayjs());
    setPaymentReference(payment.reference || "");
    setPaymentNotes(payment.notes || "");
    setPaymentProofFile(null);
    setPaymentExchangeRate(payment.exchangeRate || null);
    setShowEditPaymentModal(true);
  };

  const handleUploadPaymentProof = async (file) => {
    try {
      const storage = getStorage();
      const fileName = `payment-proofs/${invoice.id}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setPaymentProofFile(downloadURL);
      message.success("Bewijs geüpload");
    } catch (error) {
      console.error("Error uploading proof:", error);
      message.error("Fout bij uploaden bewijs");
    }
  };

  const handleAddPayment = async () => {
    if (!paymentAmount || paymentAmount <= 0) {
      message.error("Voer een geldig bedrag in");
      return;
    }

    try {
      setSavingPayment(true);

      const paymentData = {
        amount: paymentAmount,
        paymentDate: paymentDate.unix(),
        reference: paymentReference,
        notes: paymentNotes,
        proofUrl: paymentProofFile,
        exchangeRate: paymentExchangeRate,
        createdAt: dayjs().unix(),
        createdBy: email,
      };

      // Add payment to invoice
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).add({
        ...paymentData,
        invoiceId: invoice.id,
      });

      // Update invoice totals
      const newTotalPaid = (invoice.totalPaid || 0) + paymentAmount;
      const newAmountDue = invoice.totalAmount - newTotalPaid;
      const newStatus = newAmountDue <= 0 ? "paid" : newTotalPaid > 0 ? "partially_paid" : invoice.status;

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      await addHistoryEntry(
        "payment_added",
        null,
        paymentAmount,
        "payment_added",
        `Betaling van ${formatPriceByCurrency(paymentAmount, invoiceCurrency)} op ${dayjs(paymentDate).format("DD-MM-YYYY")}${paymentReference ? ` (${paymentReference})` : ""}`,
      );

      message.success("Betaling toegevoegd");
      setShowPaymentModal(false);
      resetPaymentForm();
    } catch (error) {
      console.error("Error adding payment:", error);
      message.error("Fout bij toevoegen betaling");
    } finally {
      setSavingPayment(false);
    }
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment) return;

    try {
      setSavingPayment(true);

      const updates = {
        amount: paymentAmount,
        paymentDate: paymentDate.unix(),
        reference: paymentReference,
        notes: paymentNotes,
        exchangeRate: paymentExchangeRate,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      };

      if (paymentProofFile) {
        updates.proofUrl = paymentProofFile;
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).doc(editingPayment.id).update(updates);

      // Recalculate invoice totals
      const otherPayments = payments.filter((p) => p.id !== editingPayment.id);
      const otherPaymentsTotal = otherPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const legacyPaymentsTotal = legacyPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const newTotalPaid = otherPaymentsTotal + legacyPaymentsTotal + paymentAmount;
      const newAmountDue = invoice.totalAmount - newTotalPaid;
      const newStatus = newAmountDue <= 0 ? "paid" : newTotalPaid > 0 ? "partially_paid" : invoice.status;

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      await addHistoryEntry(
        "payment_updated",
        editingPayment.amount,
        paymentAmount,
        "payment_updated",
        `Betaling gewijzigd van ${formatPriceByCurrency(editingPayment.amount, invoiceCurrency)} naar ${formatPriceByCurrency(paymentAmount, invoiceCurrency)}`,
      );

      message.success("Betaling bijgewerkt");
      setShowEditPaymentModal(false);
      setEditingPayment(null);
      resetPaymentForm();
    } catch (error) {
      console.error("Error updating payment:", error);
      message.error("Fout bij bijwerken betaling");
    } finally {
      setSavingPayment(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!invoice) {
    return <div style={{ padding: 24 }}>Factuur niet gevonden</div>;
  }

  return (
    <div style={{ padding: 24 }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(urls.purchaseOrderInvoicesList)}
        style={{ marginBottom: 16 }}
      >
        Terug naar facturen
      </Button>

      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: "bold" }}>Factuur - {invoice.invoiceNumber}</span>
            <Space>
              <Tag color={invoiceStatusColors[invoice.status] || "default"}>
                {invoiceStatusLabels[invoice.status] || invoice.status || "Ingediend"}
              </Tag>
              <Select
                size="small"
                value={invoice.status || "submitted"}
                style={{ minWidth: 140 }}
                onChange={handleUpdateInvoiceStatus}
                loading={saving}
                options={Object.entries(invoiceStatusLabels).map(([value, label]) => ({ value, label }))}
              />
            </Space>
          </div>
        }
      >
        <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Factuurnummer">{invoice.invoiceNumber}</Descriptions.Item>
          <Descriptions.Item label="Factuurdatum">
            {invoice.invoiceDate ? dayjs.unix(invoice.invoiceDate).format("DD-MM-YYYY") : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Subtotaal producten">
            {formatPriceByCurrency(invoice.subtotalProducts || 0, invoiceCurrency)}
          </Descriptions.Item>
          <Descriptions.Item label="Bijkomende kosten">
            {formatPriceByCurrency(invoice.subtotalAdditionalCosts || 0, invoiceCurrency)}
          </Descriptions.Item>
          {(invoice.discountLines?.length > 0 || (invoice.subtotalDiscounts || 0) !== 0) && (
            <Descriptions.Item label="Kortingen">
              <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                {formatPriceByCurrency(invoice.subtotalDiscounts || 0, invoiceCurrency)}
              </span>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Totaalbedrag">
            <span style={{ fontSize: 16, fontWeight: "bold", color: "#52c41a" }}>
              {formatPriceByCurrency(invoice.totalAmount || 0, invoiceCurrency)}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Betaald / Openstaand">
            <span style={{ fontWeight: "bold", color: "#1890ff" }}>
              {formatPriceByCurrency(invoice.totalPaid || 0, invoiceCurrency)}
            </span>
            {" / "}
            <span style={{ fontWeight: "bold", color: (invoice.amountDue || 0) > 0 ? "#ff4d4f" : "#52c41a" }}>
              {formatPriceByCurrency(invoice.amountDue ?? invoice.totalAmount ?? 0, invoiceCurrency)}
            </span>
          </Descriptions.Item>
          {shipment && (
            <Descriptions.Item label="Verzending">
              <Tag
                color="blue"
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`${urls.purchaseOrderShipmentDetail}${shipment.id}`)}
              >
                {shipment.shipmentNumber}
              </Tag>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Exact synchronisatie">
            {invoice.exactPurchaseInvoiceId ? (
              <div>
                {exactOutOfSync ? (
                  <div>
                    <Tag color="orange" style={{ marginBottom: 4 }}>
                      ⚠ Niet gesynchroniseerd met Exact
                    </Tag>
                    <div style={{ fontSize: 11, color: "#ff4d4f", marginBottom: 4 }}>
                      Factuur is gewijzigd na laatste synchronisatie
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      onClick={handleSyncToExact}
                      loading={syncingToExact}
                      disabled={!shipment?.sourceOrders?.[0]?.purchaseOrderNumber}
                    >
                      Opnieuw synchroniseren
                    </Button>
                  </div>
                ) : (
                  <Tag color="green">
                    ✓ Gesynchroniseerd op {dayjs.unix(invoice.exactSyncedAt).format("DD-MM-YYYY HH:mm")}
                  </Tag>
                )}
              </div>
            ) : (
              <Button
                type="primary"
                size="small"
                onClick={handleSyncToExact}
                loading={syncingToExact}
                disabled={!shipment?.sourceOrders?.[0]?.purchaseOrderNumber}
              >
                Synchroniseren naar Exact
              </Button>
            )}
          </Descriptions.Item>
        </Descriptions>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
          <h5 style={{ margin: 0 }}>Factuurregels</h5>
          <Space>
            {editingInvoice && (
              <Button size="small" icon={<PlusOutlined />} onClick={handleOpenAddLineModal}>
                Regel toevoegen
              </Button>
            )}
            {editingInvoice && (
              <Button size="small" icon={<PlusOutlined />} onClick={handleAddDiscountLine}>
                Kortingsregel toevoegen
              </Button>
            )}
            {!editingInvoice && (
              <Button size="small" onClick={handleStartEditingInvoice}>
                Factuur aanpassen
              </Button>
            )}
            {editingInvoice && (
              <Space>
                <Button size="small" onClick={handleCancelEditingInvoice}>
                  Annuleren
                </Button>
                <Button size="small" type="primary" onClick={handleSaveInvoiceEdits} loading={saving}>
                  Opslaan
                </Button>
              </Space>
            )}
          </Space>
        </div>

        {(editingInvoice ? editedInvoiceLines : invoice.lines || []).length > 0 && (
          <Table
            dataSource={(editingInvoice ? editedInvoiceLines : invoice.lines || []).map((l, i) => ({
              ...l,
              key: i,
              _index: i,
            }))}
            pagination={false}
            size="small"
            style={{ marginTop: 12, marginBottom: 16 }}
            columns={[
              {
                title: t("product"),
                dataIndex: "productName",
                render: (name, record) => (
                  <span>
                    {name}
                    {record.productColor && (
                      <span style={{ color: "#666", marginLeft: 4 }}>({record.productColor})</span>
                    )}
                  </span>
                ),
              },
              {
                title: t("quantity"),
                dataIndex: "quantity",
                render: (value, record) =>
                  editingInvoice ? (
                    <InputNumber
                      min={0}
                      value={value}
                      onChange={(v) => handleInvoiceLineChange(record._index, "quantity", v || 0)}
                      style={{ width: 80 }}
                    />
                  ) : (
                    (() => {
                      const originalQuantity = getOriginalQuantity(record._index);
                      const hasDiscrepancy = originalQuantity !== null && originalQuantity !== value;
                      return (
                        <div>
                          <div>{value}</div>
                          {hasDiscrepancy && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#ff4d4f",
                                marginTop: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <WarningOutlined style={{ fontSize: 10 }} />
                              <span>Origineel: {originalQuantity}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ),
              },
              {
                title: t("unit_price"),
                dataIndex: "unitPrice",
                render: (value, record) =>
                  editingInvoice ? (
                    <InputNumber
                      min={0}
                      step={0.01}
                      prefix={invoiceCurrency}
                      value={value}
                      onChange={(v) => handleInvoiceLineChange(record._index, "unitPrice", v || 0)}
                      style={{ width: 100 }}
                    />
                  ) : (
                    (() => {
                      const originalPrice = getOriginalUnitPrice(record._index);
                      const hasDiscrepancy = originalPrice !== null && Math.abs(originalPrice - (value || 0)) > 0.01;
                      return (
                        <div>
                          <div>{formatPriceByCurrency(value || 0, invoiceCurrency)}</div>
                          {hasDiscrepancy && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#ff4d4f",
                                marginTop: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <WarningOutlined style={{ fontSize: 10 }} />
                              <span>Origineel: {formatPriceByCurrency(originalPrice, invoiceCurrency)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ),
              },
              {
                title: "Korting",
                dataIndex: "discountPercentage",
                render: (value, record) =>
                  editingInvoice ? (
                    <InputNumber
                      min={0}
                      max={100}
                      value={value}
                      onChange={(v) => handleInvoiceLineChange(record._index, "discountPercentage", v || 0)}
                      style={{ width: 70 }}
                      suffix="%"
                    />
                  ) : value ? (
                    `${value}%`
                  ) : (
                    "-"
                  ),
              },
              {
                title: t("total"),
                dataIndex: "totalPrice",
                render: (v) => formatPriceByCurrency(v || 0, invoiceCurrency),
              },
              ...(editingInvoice
                ? [
                    {
                      title: "Acties",
                      dataIndex: "actions",
                      width: 80,
                      render: (_, record) => (
                        <Button
                          size="small"
                          type="link"
                          danger
                          onClick={() => handleRemoveInvoiceLine(record._index)}
                          style={{ padding: 0 }}
                        >
                          Verwijderen
                        </Button>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}

        {(editingInvoice ? editedAdditionalCosts : invoice.additionalCosts || []).length > 0 && (
          <>
            <h5 style={{ marginTop: 16 }}>Bijkomende kosten</h5>
            <Table
              dataSource={(editingInvoice ? editedAdditionalCosts : invoice.additionalCosts || []).map((c, i) => ({
                ...c,
                key: i,
                _index: i,
              }))}
              pagination={false}
              size="small"
              style={{ marginBottom: 16 }}
              columns={[
                { title: "Omschrijving", dataIndex: "description" },
                { title: t("quantity"), dataIndex: "quantity", render: (v) => v || 1 },
                {
                  title: t("unit_price"),
                  dataIndex: "unitPrice",
                  render: (v) => (v ? formatPriceByCurrency(v, invoiceCurrency) : "-"),
                },
                {
                  title: t("total"),
                  dataIndex: "amount",
                  render: (value, record) =>
                    editingInvoice ? (
                      <InputNumber
                        min={0}
                        step={0.01}
                        prefix={invoiceCurrency}
                        value={value}
                        onChange={(v) => handleAdditionalCostChange(record._index, "amount", v || 0)}
                        style={{ width: 100 }}
                      />
                    ) : (
                      formatPriceByCurrency(value || 0, invoiceCurrency)
                    ),
                },
              ]}
            />
          </>
        )}

        {(editingInvoice ? editedDiscountLines : invoice.discountLines || []).length > 0 && (
          <>
            <h5 style={{ marginTop: 16 }}>Kortingsregels</h5>
            <Table
              dataSource={(editingInvoice ? editedDiscountLines : invoice.discountLines || []).map((d, i) => ({
                ...d,
                key: d.id || i,
              }))}
              pagination={false}
              size="small"
              style={{ marginBottom: 16 }}
              columns={[
                {
                  title: "Omschrijving",
                  dataIndex: "description",
                  render: (value, record) =>
                    editingInvoice ? (
                      <Input
                        value={value}
                        placeholder="bv. Volumekorting"
                        onChange={(e) => handleDiscountLineChange(record.id, "description", e.target.value)}
                      />
                    ) : (
                      value || "-"
                    ),
                },
                {
                  title: "Bedrag",
                  dataIndex: "amount",
                  width: 200,
                  render: (value, record) =>
                    editingInvoice ? (
                      <InputNumber
                        max={0}
                        step={0.01}
                        prefix={invoiceCurrency}
                        value={value}
                        onChange={(v) => handleDiscountLineChange(record.id, "amount", v)}
                        style={{ width: 150 }}
                      />
                    ) : (
                      <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                        {formatPriceByCurrency(value || 0, invoiceCurrency)}
                      </span>
                    ),
                },
                ...(editingInvoice
                  ? [
                      {
                        title: "Acties",
                        dataIndex: "actions",
                        width: 80,
                        render: (_, record) => (
                          <Button
                            size="small"
                            type="link"
                            danger
                            onClick={() => handleRemoveDiscountLine(record.id)}
                            style={{ padding: 0 }}
                          >
                            Verwijderen
                          </Button>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </>
        )}

        {invoice.pdfUrl && (
          <div style={{ marginTop: 8 }}>
            <a href={invoice.pdfUrl} target="_blank" rel="noopener noreferrer">
              <Button type="primary" size="small">
                Download factuur PDF
              </Button>
            </a>
          </div>
        )}

        {invoice.remarks && (
          <div style={{ marginTop: 12, padding: 8, backgroundColor: "#fff", borderRadius: 4 }}>
            <strong>Opmerkingen:</strong>
            <div style={{ marginTop: 4, color: "#666" }}>{invoice.remarks}</div>
          </div>
        )}
      </Card>

      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: "bold" }}>Betalingen</span>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              onClick={() => {
                resetPaymentForm();
                setPaymentAmount(invoice.amountDue > 0 ? invoice.amountDue : null);
                setShowPaymentModal(true);
              }}
            >
              Betaling toevoegen
            </Button>
          </div>
        }
      >
        {/* Payments Section */}
        <div>
          {allPayments.length === 0 ? (
            <div
              style={{
                padding: 16,
                backgroundColor: "#fafafa",
                borderRadius: 8,
                textAlign: "center",
                color: "#999",
              }}
            >
              Nog geen betalingen geregistreerd
            </div>
          ) : (
            <Table
              dataSource={allPayments.map((p, i) => ({ ...p, key: i }))}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "Datum",
                  dataIndex: "paymentDate",
                  render: (v, record) => {
                    const date = v || record.paidAt || record.date;
                    return date ? dayjs.unix(date).format("DD-MM-YYYY") : "-";
                  },
                },
                {
                  title: "Bedrag",
                  dataIndex: "amount",
                  render: (v, record) => (
                    <div>
                      <strong style={{ color: "#52c41a" }}>{formatPriceByCurrency(v || 0, invoiceCurrency)}</strong>
                      {record.exchangeRate && (
                        <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                          €{(v * record.exchangeRate).toFixed(2)} @ {record.exchangeRate}
                        </div>
                      )}
                      {record.isLegacy && (
                        <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                          Aanbetaling via {record.sourceOrderNumber}
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  title: "Referentie",
                  dataIndex: "reference",
                  render: (v, record) => {
                    if (record.isLegacy) {
                      return record.sourceOrderNumber || "-";
                    }
                    return v || "-";
                  },
                },
                {
                  title: "Notities",
                  dataIndex: "notes",
                  render: (v, record) => {
                    const notes = v || record.notes || "";
                    if (record.isLegacy && !notes) {
                      return "Aanbetaling via inkooporder";
                    }
                    return notes || "-";
                  },
                },
                {
                  title: "Bewijs",
                  dataIndex: "proofUrl",
                  render: (v, record) => {
                    const proofUrl = v || record.proofUrl;
                    return proofUrl ? (
                      <a href={proofUrl} target="_blank" rel="noopener noreferrer">
                        <Button size="small" type="link" style={{ padding: 0 }}>
                          Bekijk
                        </Button>
                      </a>
                    ) : (
                      "-"
                    );
                  },
                },
                {
                  title: "Door",
                  dataIndex: "createdBy",
                  render: (v, record) => {
                    const createdBy = v || record.createdBy || record.paidBy;
                    return <span style={{ fontSize: 11, color: "#888" }}>{createdBy || "-"}</span>;
                  },
                },
                {
                  title: "Acties",
                  dataIndex: "actions",
                  render: (_, record) => (
                    <Space size="small">
                      {!record.isLegacy && (
                        <Button
                          size="small"
                          type="link"
                          onClick={() => handleEditPayment(record)}
                          style={{ padding: 0 }}
                        >
                          Bewerken
                        </Button>
                      )}
                      {record.isLegacy && <span style={{ fontSize: 11, color: "#999" }}>Legacy</span>}
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </div>
      </Card>

      {invoice.history && invoice.history.length > 0 && (
        <Card title="Wijzigingsgeschiedenis" size="small" style={{ marginTop: 16 }}>
          <Collapse defaultActiveKey={[]}>
            <Panel header="Toon geschiedenis" key="1">
              <div style={{ maxHeight: 400, overflowY: "auto", paddingTop: 20 }}>
                <Timeline>
                  {[...invoice.history].reverse().map((entry, idx) => (
                    <Timeline.Item key={idx}>
                      <div>
                        <p style={{ margin: 0 }}>
                          <strong>
                            {entry.action === "status_change" && "Statuswijziging"}
                            {entry.action === "invoice_edit" && "Factuuraanpassing"}
                            {entry.action === "payment_added" && "Betaling toegevoegd"}
                            {entry.action === "payment_updated" && "Betaling bijgewerkt"}
                          </strong>
                        </p>
                        <p style={{ margin: 0, fontSize: 12, color: "#888" }}>
                          {dayjs.unix(entry.changedAt).format("DD-MM-YYYY HH:mm")} - {entry.changedBy}
                        </p>
                        {entry.field && (
                          <p style={{ margin: 0, fontSize: 12 }}>
                            <strong>{entry.field}:</strong>{" "}
                            {entry.oldValue !== null && entry.oldValue !== undefined && (
                              <>
                                {entry.oldValue} → {entry.newValue}
                              </>
                            )}
                            {entry.oldValue === null && entry.newValue}
                          </p>
                        )}
                        {entry.metadata && (
                          <div style={{ marginTop: 4, fontSize: 11, color: "#666" }}>{entry.metadata}</div>
                        )}
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </div>
            </Panel>
          </Collapse>
        </Card>
      )}

      {/* Add Payment Modal */}
      <Modal
        title="Betaling toevoegen"
        open={showPaymentModal}
        onOk={handleAddPayment}
        onCancel={() => {
          setShowPaymentModal(false);
          resetPaymentForm();
        }}
        okText="Toevoegen"
        cancelText="Annuleren"
        confirmLoading={savingPayment}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bedrag ({invoiceCurrency}):</label>
            <InputNumber
              min={0}
              step={0.01}
              value={paymentAmount}
              onChange={setPaymentAmount}
              style={{ width: "100%" }}
              prefix={invoiceCurrency}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Datum:</label>
            <DatePicker value={paymentDate} onChange={setPaymentDate} style={{ width: "100%" }} format="DD-MM-YYYY" />
          </div>
          {invoiceCurrency !== "EUR" && (
            <div>
              <label style={{ display: "block", marginBottom: 4 }}>Wisselkoers (naar EUR):</label>
              <InputNumber
                min={0}
                step={0.0001}
                value={paymentExchangeRate}
                onChange={setPaymentExchangeRate}
                style={{ width: "100%" }}
                placeholder="Bijv. 0.92"
              />
            </div>
          )}
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Referentie:</label>
            <Input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Notities:</label>
            <TextArea value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} rows={3} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bewijs (PDF/afbeelding):</label>
            <Upload
              beforeUpload={(file) => {
                handleUploadPaymentProof(file);
                return false;
              }}
              onRemove={() => setPaymentProofFile(null)}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Upload bewijs</Button>
            </Upload>
          </div>
        </Space>
      </Modal>

      {/* Edit Payment Modal */}
      <Modal
        title="Betaling bewerken"
        open={showEditPaymentModal}
        onOk={handleUpdatePayment}
        onCancel={() => {
          setShowEditPaymentModal(false);
          setEditingPayment(null);
          resetPaymentForm();
        }}
        okText="Opslaan"
        cancelText="Annuleren"
        confirmLoading={savingPayment}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bedrag ({invoiceCurrency}):</label>
            <InputNumber
              min={0}
              step={0.01}
              value={paymentAmount}
              onChange={setPaymentAmount}
              style={{ width: "100%" }}
              prefix={invoiceCurrency}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Datum:</label>
            <DatePicker value={paymentDate} onChange={setPaymentDate} style={{ width: "100%" }} format="DD-MM-YYYY" />
          </div>
          {invoiceCurrency !== "EUR" && (
            <div>
              <label style={{ display: "block", marginBottom: 4 }}>Wisselkoers (naar EUR):</label>
              <InputNumber
                min={0}
                step={0.0001}
                value={paymentExchangeRate}
                onChange={setPaymentExchangeRate}
                style={{ width: "100%" }}
                placeholder="Bijv. 0.92"
              />
            </div>
          )}
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Referentie:</label>
            <Input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Notities:</label>
            <TextArea value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} rows={3} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bewijs (PDF/afbeelding):</label>
            <Upload
              beforeUpload={(file) => {
                handleUploadPaymentProof(file);
                return false;
              }}
              onRemove={() => setPaymentProofFile(null)}
              maxCount={1}
              defaultFileList={
                editingPayment?.proofUrl
                  ? [{ uid: "1", name: "Bewijs", status: "done", url: editingPayment.proofUrl }]
                  : []
              }
            >
              <Button icon={<UploadOutlined />}>Upload bewijs</Button>
            </Upload>
          </div>
        </Space>
      </Modal>

      {/* Add Line Modal */}
      <Modal
        title="Factuurregel toevoegen"
        open={showAddLineModal}
        onOk={handleAddInvoiceLine}
        onCancel={handleCloseAddLineModal}
        okText="Toevoegen"
        cancelText="Annuleren"
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Product:</label>
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder="Selecteer een product"
              value={selectedProduct?.id}
              onChange={handleProductSelect}
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              options={products.map((p) => ({
                value: p.id,
                label: `${p.name}${p.color ? ` (${p.color})` : ""} - ${p.code || ""}`,
              }))}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Aantal:</label>
            <InputNumber min={0} value={newLineQuantity} onChange={setNewLineQuantity} style={{ width: "100%" }} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Prijs per stuk ({invoiceCurrency}):</label>
            <InputNumber
              min={0}
              step={0.01}
              value={newLineUnitPrice}
              onChange={setNewLineUnitPrice}
              style={{ width: "100%" }}
              prefix={invoiceCurrency}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Korting (%):</label>
            <InputNumber
              min={0}
              max={100}
              value={newLineDiscount}
              onChange={setNewLineDiscount}
              style={{ width: "100%" }}
              suffix="%"
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default PurchaseOrderInvoiceDetail;
